/**
 * The clock the `timings` array is stamped on (`transcript_phrase_N`, `placement_logs`).
 *
 * Not wall clock and not phrase-relative: it is milliseconds into the story's recorded
 * audio, with intervention time, page-turn gaps, and trimmed silence subtracted out —
 * which is what makes it line up with the sliced audio `calculatePhraseSplits` produces.
 *
 * ReaderManager owns every input to that arithmetic (`recordingStart`,
 * `totalTimeSpentIntervening`, `timeBetweenPages`, and Speech's silent-segment offset),
 * so it registers the reader here rather than exporting the values. The local ASR layer
 * has none of them and stamps its raw snapshots through this module, so raw engine
 * output and the final transcript share one axis and can be compared entry to entry.
 *
 * The registry keeps the dependency inverted: nothing here imports ReaderManager, and
 * consumers that run without a mounted reader (StudentPreChecks, tests) get null and
 * fall back to their own axis.
 */

let read = null;

/**
 * Registers the reader. Called by the component that owns the timing inputs.
 *
 * Returns the registration so the caller can unregister only its own: React can commit
 * an incoming component's mount before the outgoing one's unmount (a route swap spanning
 * commits, StrictMode's dev double-invoke), and an unconditional clear from the departing
 * instance would silently drop the arriving instance's reader. The failure mode is
 * invisible — every raw snapshot quietly degrades to the phrase axis with a
 * plausible-looking label — so ownership is checked rather than assumed.
 * @param {Function} reader Returns milliseconds on the placement axis.
 * @returns {Function|null} Token to pass to clearPlacementClock.
 */
export function setPlacementClock(reader) {
  read = typeof reader === 'function' ? reader : null;
  return read;
}

/**
 * Unregisters the reader, so a stale closure cannot outlive its component. No-ops when
 * the slot already belongs to a newer registration (see setPlacementClock).
 * @param {Function} [token] The value setPlacementClock returned.
 * @returns {void}
 */
export function clearPlacementClock(token) {
  if (!token || read === token) {
    read = null;
  }
}

/**
 * Current time on the placement axis.
 *
 * Returns null when no reader is registered, or when the reader cannot produce a
 * finite value (recording has not started, or Speech is mid-teardown) — callers
 * decide their own fallback rather than being handed a misleading 0.
 *
 * A throwing reader is treated the same way. Callers are on the decode-result path,
 * where a propagating error would be counted as an inference failure and tear local ASR
 * down; the reader reaches into recorder state that can legitimately disappear
 * mid-teardown, which is the window this registry exists to absorb. Matches the guarantee
 * localAsrLifecycle.record and voskAsr.logToConsole make for their own logging.
 * @returns {number|null}
 */
export function placementTimeMs() {
  if (!read) {
    return null;
  }
  let value;
  try {
    value = read();
  } catch (error) {
    return null;
  }
  return Number.isFinite(value) ? value : null;
}
